import { FACEBOOK_GROUP_LIVE_KEYS, type FacebookGroupLiveInput } from '@sbc/domain-models/gateway';
import { Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, type MouseEvent, type WheelEvent, useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import {
  useLiveFrame,
  useSendLiveInput,
  useStartLiveView,
  useStopLiveView,
} from '@/lib/api/facebook-group';

const SCROLL_STEP = 600;

export function FacebookGroupLiveViewPage({ connectionId }: { connectionId: string }) {
  const navigate = useNavigate();
  const start = useStartLiveView(connectionId);
  const stop = useStopLiveView(connectionId);
  const send = useSendLiveInput(connectionId);
  const started = start.isSuccess && !stop.isSuccess;
  const frame = useLiveFrame(connectionId, started);
  const [text, setText] = useState('');
  const [address, setAddress] = useState('');
  const { mutate: openLiveView } = start;

  useEffect(() => {
    openLiveView();
  }, [openLiveView]);

  const input = (value: FacebookGroupLiveInput) => {
    if (!send.isPending) {
      send.mutate(value);
    }
  };

  const onClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (!frame.data) {
      return;
    }
    const bounds = event.currentTarget.getBoundingClientRect();
    input({
      type: 'click',
      x: ((event.clientX - bounds.left) / bounds.width) * frame.data.width,
      y: ((event.clientY - bounds.top) / bounds.height) * frame.data.height,
    });
  };

  const onWheel = (event: WheelEvent<HTMLButtonElement>) => {
    input({ type: 'scroll', deltaY: Math.sign(event.deltaY) * SCROLL_STEP });
  };

  const onType = (event: FormEvent) => {
    event.preventDefault();
    if (text.length > 0) {
      input({ type: 'text', text });
      setText('');
    }
  };

  const onNavigate = (event: FormEvent) => {
    event.preventDefault();
    if (address.length > 0) {
      input({ type: 'navigate', url: address });
    }
  };

  const onDone = () => {
    stop.mutate(undefined, {
      onSuccess: () =>
        navigate({ to: '/gateway/connections/$connectionId', params: { connectionId } }),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Facebook group live view</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            You are driving the bot&apos;s browser. Polling and replies wait until you press Done,
            or until 10 minutes pass without activity.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={onDone} disabled={!started || stop.isPending}>
            Done
          </Button>
          <Button asChild variant="outline">
            <Link to="/gateway/connections/$connectionId" params={{ connectionId }}>
              Back
            </Link>
          </Button>
        </div>
      </div>

      {start.isPending && <p className="text-sm">Opening the browser…</p>}
      {start.isError && (
        <div className="space-y-2">
          <p className="text-sm text-[var(--color-destructive)]">{start.error.message}</p>
          <Button variant="outline" size="sm" onClick={() => openLiveView()}>
            Try again
          </Button>
        </div>
      )}
      {stop.isError && (
        <p className="text-sm text-[var(--color-destructive)]">{stop.error.message}</p>
      )}

      {started && (
        <Card>
          <CardHeader className="space-y-3">
            <CardTitle className="flex items-center gap-2 text-base">
              Browser
              {start.data.sessionStatus === 'ok' ? (
                <Badge variant="secondary">Logged in</Badge>
              ) : (
                <Badge variant="outline">Needs login</Badge>
              )}
            </CardTitle>
            <form className="flex gap-2" onSubmit={onNavigate}>
              <Input
                value={address}
                placeholder={frame.data?.url ?? 'https://www.facebook.com/'}
                onChange={(event) => setAddress(event.target.value)}
                aria-label="Address"
              />
              <Button type="submit" variant="outline">
                Go
              </Button>
            </form>
          </CardHeader>
          <CardContent className="space-y-3">
            {frame.isError && (
              <p className="text-sm text-[var(--color-destructive)]">{frame.error.message}</p>
            )}
            {send.isError && (
              <p className="text-sm text-[var(--color-destructive)]">{send.error.message}</p>
            )}
            {frame.data ? (
              <button
                type="button"
                className="block w-full cursor-pointer overflow-hidden rounded-md border p-0"
                onClick={onClick}
                onWheel={onWheel}
              >
                <img
                  src={`data:image/jpeg;base64,${frame.data.image}`}
                  alt="The bot's browser"
                  className="block w-full"
                />
              </button>
            ) : (
              <p className="text-sm">Loading the screen…</p>
            )}
            <form className="flex gap-2" onSubmit={onType}>
              <div className="flex-1">
                <PasswordInput
                  value={text}
                  autoComplete="off"
                  placeholder="Click a field above, then type here and press Type"
                  onChange={(event) => setText(event.target.value)}
                  aria-label="Text to type"
                />
              </div>
              <Button type="submit" variant="outline">
                Type
              </Button>
            </form>
            <div className="flex flex-wrap gap-2">
              {FACEBOOK_GROUP_LIVE_KEYS.map((key) => (
                <Button
                  key={key}
                  variant="outline"
                  size="sm"
                  onClick={() => input({ type: 'key', key })}
                >
                  {key}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
